// src/hooks/useNotificationSocket.js
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addRealtimeNotification } from "store/slices/notificationSlice";
import voice from "assets/voice/notifcation.mp3";

const useNotificationSocket = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth.user);
  const token = useSelector((s) => s.auth.token); // Use Redux token (safe & reactive)

  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const notificationSound = useRef(new Audio(voice));
  notificationSound.current.volume = 0.8;

  const API_SOCKET_URL = process.env.REACT_APP_SOCKET_BASE_URL;

  // Check if sound is enabled (respects live changes)
  const isSoundEnabled = () =>
    localStorage.getItem("notificationSoundEnabled") !== "false";

  const playSound = () => {
    if (isSoundEnabled()) {
      notificationSound.current.currentTime = 0;
      notificationSound.current.play().catch(() => {});
    }
  };

  useEffect(() => {
    // If not logged in → clean up and don't connect
    if (!user?.id || !token) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      return;
    }
    const wsUrl = `${API_SOCKET_URL}/notifications/?token=${token}`;
    let shouldReconnect = true;

    const connect = () => {
      if (!shouldReconnect) return;

      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log("Notification WebSocket connected");
      };

      socket.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);

          if (data.type === "notification") {
            dispatch(addRealtimeNotification(data.data));
            playSound(); // Respects current sound setting
          } else if (data.type === "initial_state") {
            data.data.notifications.forEach((notif) =>
              dispatch(addRealtimeNotification(notif))
            );
          }
        } catch (err) {
          console.error("Notification WS parse error:", err);
        }
      };

      socket.onclose = (event) => {
        console.log("Notification WS closed", event.code, event.reason);
        socketRef.current = null;

        if (shouldReconnect) {
          console.log("Reconnecting notification socket in 3s...");
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        }
      };

      socket.onerror = (err) => {
        console.error("Notification WS error:", err);
        socket.close(); // will trigger onclose → reconnect
      };
    };

    connect();

    // Listen to localStorage changes (from settings page)
    const handleStorage = (e) => {
      if (e.key === "notificationSoundEnabled") {
        console.log("Sound setting changed →", isSoundEnabled() ? "ON" : "OFF");
      }
    };
    window.addEventListener("storage", handleStorage);

    // Cleanup on unmount or logout
    return () => {
      shouldReconnect = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }

      window.removeEventListener("storage", handleStorage);
    };
  }, [user?.id, token, dispatch]);
};

export default useNotificationSocket;
